import * as R from 'ramda';
import React from 'react';

/**
 * Рекурсивно удаляет указанное свойство из объекта и всех его вложенных объектов.
 *
 * Модифицирует исходный объект (побочный эффект)!
 * Предназначена для очистки данных, например, от `__typename`, добавляемого Apollo Client.
 *
 * @param {Object} obj — объект, из которого удаляется свойство. Если `null`/`undefined` — возвращается как есть.
 * @param {string} key — имя удаляемого свойства.
 * @returns {Object} тот же объект (`obj`), с удалёнными полями `key` на всех уровнях.
 */
const omitProp = (obj, key) => {
  if (!obj || !key) return obj;
  //var obj = R.clone(o);
  for (var i in obj) {
    if (!obj.hasOwnProperty(i)) continue;
    if (typeof obj[i] === 'object') {
      omitProp(obj[i], key);
    } else if (i === key) {
      delete obj[key];
    }
  }
  return obj;
};

/**
 * Преобразует простой массив значений в формат справочника: `{code, value}`.
 *
 * @param {Array<string|number>} array — исходный массив.
 * @param {boolean} [withEmpty=false] — добавить ли пустой элемент `{code: null, value: '-'}` в начало.
 * @returns {Array<{code: *, value: *}>}
 */
export const createNsiList = (array, withEmpty = false) => {
  //    let i =1
  if (withEmpty) {
    let empty = [{ code: null, value: '-' }];
    return empty.concat(array.map((item) => ({ code: item, value: item })));
  } else return array.map((item) => ({ code: item, value: item }));
  /*
      let res={}
      res.code=i++
      res.value=item
      return res//({code:item, value:item})
    */
};

/**
 * Преобразует словарь `{key: value}` в массив `{code: key, value: value}`.
 *
 * @param {Object.<string, string>} dict — объект-словарь.
 * @param {boolean} [withEmpty=false] — добавить ли пустой элемент `{code: null, value: '-'}`.
 * @returns {Array<{code: string, value: string}>}
 */
export const createNsiDict = (dict, withEmpty = false) => {
  let res = withEmpty ? [{ code: null, value: '-' }] : [];
  return res.concat(Object.keys(dict).map((k) => ({ code: k, value: dict[k] })));
};

/**
 * Обратное преобразование словаря: `{code: value}` → `{code: value, value: code}`.
 * Полезно, когда значение — это человекочитаемый ID, а ключ — отображаемое имя.
 *
 * @param {Object.<string, string>} dict — объект-словарь.
 * @param {boolean} [withEmpty=false] — добавить ли пустой элемент.
 * @returns {Array<{code: string, value: string}>}
 */
export const createNsiReverseDict = (dict, withEmpty = false) => {
  let res = withEmpty ? [{ code: null, value: '-' }] : [];
  return res.concat(Object.keys(dict).map((k) => ({ code: dict[k], value: k })));
};

/**
 * Условное создание списка НСИ: возвращает список только если `data[attr]` существует.
 *
 * @param {Object} data — контекстный объект.
 * @param {string} attr — имя поля, наличие которого проверяется.
 * @param {Array} array — исходный массив для преобразования.
 * @returns {Array<{code: *, value: *}>}
 */
export const createNsiListCond = (data, attr, array) => {
  //let i =1
  return data && data[attr] ? array.map((item) => ({ code: item, value: item })) : [];
  /*
      let res={}
      res.code=i++
      res.value=item
      return res//({code:item, value:item})
    */
};

/**
 * Преобразует массив объектов в список `{code, value}`, извлекая значение по указанному полю.
 *
 * @param {Array<Object>|null|undefined} array — массив объектов.
 * @param {string} prop — имя свойства, из которого берётся значение.
 * @param {boolean} [withEmpty=false] — добавить ли пустой элемент `{code: null, value: null}`.
 * @returns {Array<{code: *, value: *}>}
 */
export const createList = (array, prop, withEmpty = false) => {
  //    let i =1
  if (array === null || array === undefined || array.length === 0) return [];
  let res = array.map((item) => ({ code: item[prop], value: item[prop] }));
  if (withEmpty) {
    return [{ code: null, value: null }, ...res];
  } else return res;
  /*
						let res={}
				res.code=i++
				res.value=item
				return res//({code:item, value:item})
						*/
};

/**
 * Извлекает значения указанного поля из массива объектов → массив строк.
 *
 * @param {Array<Object>|null|undefined} array — массив объектов.
 * @param {string} propName — имя строкового поля.
 * @param {boolean} [withEmpty=false] — добавить ли пустую строку `''` в начало.
 * @returns {Array<string>}
 */
export const createStrArray = (array, propName, withEmpty = false) => {
  if (array === null || array === undefined || array.length === 0) return [];
  let res = array.map((item) => item[propName]);
  if (withEmpty) {
    return ['', ...res];
  } else return res;
};

/** 
    Функция для удаления property из объектов
    Нужна, например, для исправления плохого поведения Apollo, добавлябщего __typedef
    https://github.com/apollographql/apollo-feature-requests/issues/6

    @param obj - объект (побочный эффект! Объект модифицируется)
    @param key - название property, которое необъодимо удалить
*/
export const stripTypenames = (obj, key = '__typename') => omitProp(R.clone(obj), key); //Пришлось сделать с клонированием, т.к. портал начал глючить (новый Реакт?)

/**
  Функция для преобразования полей в строку с разделителями
  @param data {Object} - объект с данными
  @param fields {[String]}- массив с именами полей
  @param separator {String} - разделитель 
*/
export const fieldsToString = (data, fields, separator) => {
  return fields.reduce((acc, field) => {
    var val = Array.isArray(field) ? R.pathOr('', field, data) : R.propOr('', field, data);
    return acc + (val === '' || acc === '' ? '' : separator) + val;
  }, '');
};

/**
  Функция для скачивания файла
  @param url
  @param defaultName - имя по умолчанию, под которым сохраняется полученный файла
  @param fetchconfig -  конфигурация fetch
*/
export const download = (url, defaultName, fetchConfig) => {
  fetch(url, fetchConfig)
    .then((res) => {
      if (!res.ok) {
        const msg = `Ошибка получения данных: ${res.status} ${res.statusText}`;
        console.error(msg);
        throw new Error(msg);
      }

      // Пытаемся извлечь имя файла из заголовка Content-Disposition
      let filename = defaultName;
      const contentDisposition = res.headers.get('content-disposition');

      if (contentDisposition) {
        // Сначала ищем filename*=UTF-8''
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (filenameStarMatch && filenameStarMatch[1]) {
          filename = decodeURIComponent(filenameStarMatch[1].trim());
        } else {
          // Иначе ищем обычное filename="..." или filename=...
          const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].trim();
          }
        }
      }

      return res.blob().then((blob) => ({blob, filename}));
    })
    .then(({blob, filename}) => {
      // Создаем ссылку и инициируем скачивание
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Удаляем элемент и освобождаем память
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    })
    .catch((err) => {
      console.error('Ошибка экспорта:', err);
      alert(`Ошибка экспорта: ${err.message || err}`);
    })
}

/**
 * Преобразует плоский массив узлов дерева в ассоциативный массив (хэш-таблицу) по `code`.
 *
 * Добавляет виртуальный корневой узел `'/'`, упрощая навигацию.
 *
 * @param {Array<{code: string, parent: string|null}>} data — массив узлов.
 * @returns {Object.<string, Object>} — объект вида `{ code → node }`, включая `res['/']`.
 */
export const prepareTree = (data) => {
  let res = [];
  let root = { name: '/', code: '/', children: [] };

  data.forEach((el) => {
    res[el.code] = el;
    if (el.parent === null) root.children.push(el.code);
  });
  res['/'] = root;
  return res;
};

/* 
 Возвращает функцию, которая не будет срабатывать, пока продолжает вызываться.
 Она сработает только один раз через wait миллисекунд после последнего вызова.
 Если ей передан аргумент `immediate`, то она будет вызвана один раз сразу после
 первого запуска.

export const debounce=(func, wait, immediate=true) => {
    let timeout;
    return (...args) => {
	//let context = this, args = arguments;
	let later = () => {
	    timeout = null;
	    if (!immediate) func(... args);
	}
	let callNow = immediate && !timeout;
	clearTimeout(timeout);
	timeout = setTimeout(later, wait);
	if(callNow) func(... args);
    }
}
*/
/** Originally inspired by  David Walsh (https://davidwalsh.name/javascript-debounce-function)

 Returns a function, that, as long as it continues to be invoked, will not
 be triggered. The function will be called after it stops being called for
 `wait` milliseconds.
*/
/*
export const debounce = (f, wait) => {
  let timeout;

  // This is the function that is returned and will be executed many times
  return (...args) => {
    const later = () => {
      // The callback function to be executed after the debounce time has elapsed
      timeout = null; // null timeout to indicate the debounce ended
      f(...args); // Execute the callback
    };
    clearTimeout(timeout); // This will reset the waiting every function execution.  This is the step that prevents the function from being executed because it will never reach the inside of the previous setTimeout
    timeout = setTimeout(later, wait); // Restart the debounce waiting period. setTimeout returns a truthy value (it differs in web vs Node)
  };
};
*/
export const KBD_TIMEOUT = 500; // таймаут по умолчанию для debounce
/**
 * Задержка вызова функции до тех пор, пока она не перестанет вызываться в течение `timeout` мс.
 *
 * Отложенное выполнение после **последнего** вызова.
 *
 * @param {Function} func — функция для debounce.
 * @param {number} [timeout=KBD_TIMEOUT] — задержка в миллисекундах.
 * @returns {Function} задебаунсенная функция.
 */
export function debounce(func, timeout = KBD_TIMEOUT) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

/**
 * Безопасный парсинг JSON: не выбрасывает исключение при некорректном JSON.
 *
 * @param {string} value — строка JSON.
 * @returns {*} распарсенный объект или `null` при ошибке.
 */
export const safeParseJSON = (value) => {
  let res = null;
  try {
    res = JSON.parse(value);
  } catch (e) {}
  return res;
};

/**
 * Циклическое переключение: `null` → `true` → `false` → `null` …
 *
 * @param {boolean|null} v — текущее значение.
 * @returns {boolean|null}
 */
export const toggleThreeState = (v) => (v === null ? true : v ? false : null);

/**
 * Циклическое переключение числового трёхсостояния: `-1` → `1` → `0` → `-1` …
 *
 * @param {number} v — текущее значение (`-1`, `0`, `1`).
 * @returns {number}
 */
export const toggleThreeStateInt = (v) => (v === -1 ? 1 : v ? 0 : -1);

/**
 * Фабрика accessor’а для `react-table`: извлекает массив, берёт поле у каждого элемента → объединяет через `, `.
 *
 * @param {string} array — имя массива в `row`.
 * @returns {Function} `(field: string) => (row: Object) => string`
 */
export const arrayString = (array) => (field) => (row) =>
  row[array] ? row[array].map((el) => (el ? el[field] : null)).join(', ') : '';

/**
 * Фабрика accessor’а с фильтрацией: сначала фильтрует массив, затем извлекает и объединяет.
 *
 * @param {string} array — имя массива в `row`.
 * @returns {Function} `(filter: Function) => (field: string) => (row: Object) => string`
 */
export const arrayFilterString = (array) => (filter) => (field) => (row) => {
  return row[array]
    ? row[array]
        .filter(filter)
        .map((el) => (el ? el[field] : null))
        .join(', ')
    : '';
};

/**
 * Ревизор (сериализатор) для `JSON.stringify`, преобразующий поля `date` в строку.
 *
 * @param {string} k — имя свойства.
 * @param {*} v — значение.
 * @returns {*} модифицированное значение.
 */
export const revewerSz = (k, v) => {
  switch (k) {
    //case '_id':
    case 'date':
      return new String(v);
    default:
      return v;
  }
};

/**
 * Ревизор (десериализатор) для `JSON.parse`, восстанавливающий:
 * - `_id` из строки `'ObjectId(...)'`
 * - `date` → `Date`
 *
 * @param {string} k — имя свойства.
 * @param {*} v — значение.
 * @returns {*} восстановленное значение.
 */
export const reviewerDsz = (k, v) => {
  switch (k) {
    case '_id':
      return v.replace('ObjectId(', '').replace(')', '');
    case 'date':
      return new Date(v);
    default:
      return v;
  }
};

/**
 * Форматирует дату в локальную строку (ru-RU).
 *
 * @param {string|Date} dateStr — строка или объект Date.
 * @returns {string} например: `"10.12.2025, 15:30:45"`
 */
export const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleString('ru-RU')
}

/**
 * Проверяет наличие ошибок валидации по пути
 * Ищет ошибки, которые начинаются с указанного пути (включая вложенные)
 * 
 * @param {Array} errors - массив ошибок валидации
 * @param {Array} path - путь к полю: ['doc', 'expData', 'ТТ']
 * @param {Array} [additionalPath=[]] - дополнительный путь для расширения: ['паспорт']
 * @returns {boolean} true если есть ошибки на этом пути
 */
export const useHasPathErrors = (errors, path, additionalPath = []) => {
  return React.useMemo(() => {
    if (!errors || !Array.isArray(errors)) return false;
    if (!path || !Array.isArray(path)) return false;

    const fullPath = additionalPath.length > 0 
      ? [...path, ...additionalPath]
      : path;

    const pathStr = fullPath.reduce((acc, curr) => 
      acc + (typeof curr === 'string' ? '.' + curr : `.[${curr}]`), ''
    )

    return errors.some(err => {
      if (!err?.dataPath) return false;
      return err.dataPath.startsWith(pathStr);
    });
  }, [errors, path, additionalPath]);
};

/**
 * Глобальный обработчик фокуса для Dialog
 * Вызывается автоматически при событии onEntered
 * @param {HTMLElement} node - корневой элемент диалога
 */
export const handleDialogEntered = (node) => {
  if (!node) return;

  const explicit = node.querySelector('[data-default-focus]');
  if (explicit) {
    explicit.focus({preventScroll: true});
    return;
  }
}